<template>
  <div class="pc-detail">
    <section class="pc-container breadcrumb">
      <span>首页</span>
      <img :src="icons.right" alt="">
      <span>{{ product.categoryName || '-' }}</span>
      <template v-if="product.subCategoryName">
        <img :src="icons.right" alt="">
        <span>{{ product.subCategoryName }}</span>
      </template>
      <img :src="icons.right" alt="">
      <span class="current">{{ productTitle(product) }}</span>
    </section>

    <section class="pc-container detail-layout">
      <div class="main-column">
        <article class="summary-card">
          <div class="gallery" @mouseenter="stopCarousel" @mouseleave="startCarousel">
            <div class="gallery-stage">
              <img class="main-image" :src="activeGalleryImage" :alt="productTitle(product)" @error="handleProductImageError" @click="previewVisible = true">
              <button v-if="product.images.length > 1" class="stage-arrow prev" aria-label="上一张" @click="prevImage">‹</button>
              <button v-if="product.images.length > 1" class="stage-arrow next" aria-label="下一张" @click="nextImage">›</button>
              <span v-if="product.images.length > 1" class="gallery-counter">{{ activeImageIndex + 1 }}/{{ product.images.length }}</span>
            </div>
            <div class="thumb-row">
              <button class="arrow" :disabled="product.images.length <= 1" @click="prevImage">‹</button>
              <img
                v-for="(image, index) in product.images"
                :key="`${image}-${index}`"
                :class="{ active: index === activeImageIndex }"
                :src="image"
                @error="handleGalleryImageError(index)"
                :alt="`${productTitle(product)}图片${index + 1}`"
                @click="selectImage(index)"
              >
              <button class="arrow" :disabled="product.images.length <= 1" @click="nextImage">›</button>
            </div>
            <p>注：图像仅供参考，请参阅产品规格</p>
          </div>

          <div class="summary-info">
            <div class="title-row">
              <h1>{{ productTitle(product) }}</h1>
            </div>
            <p>商品编码：{{ productCode }}</p>
            <dl>
              <template v-for="item in baseInfo">
                <dt :key="`dt-${item.label}`">{{ item.label }}：</dt>
                <dd :key="`dd-${item.label}`">{{ item.value }}</dd>
              </template>
            </dl>
          </div>
        </article>

        <article id="product-files" class="param-card">
          <div class="tabs">
            <button :class="{ active: activeDetailTab === 'params' }" @click="activeDetailTab = 'params'">产品详情</button>
            <button v-if="files.length || activeDetailTab === 'files'" :class="{ active: activeDetailTab === 'files' }" @click="activeDetailTab = 'files'">产品文件</button>
          </div>
          <template v-if="activeDetailTab === 'params'">
            <h2>{{ productTitle(product) }}产品详情</h2>
            <div class="product-overview">
              <h3>产品概述</h3>
              <p>{{ product.desc || '暂无产品概述' }}</p>
            </div>
            <div class="param-table">
            <table>
              <thead>
                <tr><th>属性</th><th>参数值</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in detailParams" :key="`${item.name}-${index}`">
                  <td>{{ item.name }}</td>
                  <td>{{ item.value }}</td>
                </tr>
              </tbody>
            </table>
            </div>
          </template>
          <template v-else-if="activeDetailTab === 'files'">
            <h2>{{ productTitle(product) }}产品文件</h2>
            <div v-if="files.length" class="file-content">
              <div v-if="pdfFiles.length" class="pdf-list">
                <section v-for="file in pdfFiles" :key="file.id || file.fileUrl" class="pdf-preview">
                  <div class="pdf-toolbar">
                    <span>{{ file.fileName }}</span>
                    <a :href="file.fileUrl" target="_blank" rel="noopener noreferrer">打开 PDF</a>
                  </div>
                  <iframe :src="file.fileUrl" :title="file.fileName" loading="lazy"></iframe>
                </section>
              </div>
              <div v-if="imageFiles.length" class="file-image-grid">
                <a v-for="file in imageFiles" :key="file.id || file.fileUrl" :href="file.fileUrl" target="_blank" rel="noopener">
                  <img :src="file.fileUrl" :alt="file.fileName">
                  <span>{{ file.fileName }}</span>
                </a>
              </div>
              <div v-if="downloadFiles.length" class="download-list">
                <a v-for="file in downloadFiles" :key="file.id || file.fileUrl" :href="file.fileUrl" target="_blank" rel="noopener">
                  <img :src="icons.file" alt="">{{ file.fileName }}（{{ file.fileType }}）
                </a>
              </div>
            </div>
            <div v-else class="detail-empty">暂无产品文件</div>
          </template>
        </article>
      </div>

      <aside class="purchase-card">
        <h2>最新价格</h2>
        <div v-if="product.hasPrice" class="price-table">
          <div class="price-head"><span>梯度</span><span>单价(含税)</span></div>
          <p v-for="(row, index) in displayTiers" :key="row.minQuantity" :class="{ first: index === 0 }">
            <span>{{ row.minQuantity }}+</span>
            <b>¥ {{ row.price }}</b>
          </p>
          <button @click="goSourcing">量大可议价</button>
        </div>
        <div v-else class="detail-empty no-price">暂无价格/联系询价</div>

        <h2>库存</h2>
        <div class="stock-table">
          <div><span>库存数量</span></div>
          <p><b>{{ product.stock }}</b></p>
        </div>

        <div class="buy-info">
          <strong>购买数量</strong>
        </div>
        <div class="quantity-line">
          <div class="stepper">
            <button @click="changeQuantity(-product.increment)"><img :src="icons.minus" alt=""></button>
            <quantity-input :value="quantity" :min="product.min || 1" :max="product.stock" :disabled="!canPurchase" @input="handleQuantityInput" />
            <button @click="changeQuantity(product.increment)"><img :src="icons.plus" alt=""></button>
          </div>
          <span v-if="product.hasPrice">单价 <b>¥ {{ currentPrice }}</b></span>
          <span v-else class="no-price">暂无价格/联系询价</span>
        </div>
        <div v-if="product.hasPrice" class="total-line">合计：<b>¥ {{ totalAmount }}</b></div>
        <div class="actions">
          <button class="cart" :disabled="product.hasPrice && !canPurchase" @click="addCurrentToCart">{{ purchaseButtonText }}</button>
          <button v-if="product.hasPrice" class="buy" :disabled="!canPurchase" @click="buyNow">直接购买</button>
        </div>
      </aside>
    </section>
    <div v-if="previewVisible" class="image-preview-mask" @click="previewVisible = false">
      <button class="preview-close" aria-label="关闭大图" @click="previewVisible = false">×</button>
      <button v-if="product.images.length > 1" class="preview-arrow preview-prev" aria-label="上一张" @click.stop="prevImage">‹</button>
      <img :src="activeGalleryImage" :alt="productTitle(product)" @error="handleProductImageError" @click.stop>
      <button v-if="product.images.length > 1" class="preview-arrow preview-next" aria-label="下一张" @click.stop="nextImage">›</button>
    </div>
  </div>
</template>

<script>
  import { message } from 'ant-design-vue'
  import { getPcProductDetail, createPcTrackingEvent } from '@/api/pc'
  import commerce from '@/utils/pcCommerce'
  import QuantityInput from '@/components/pc/QuantityInput.vue'
  import { addCartItem, prepareBuyNowItem } from '@/utils/pcCart'
  import { decodeRouteCode } from '@/utils/pcRouteCode'
  import { productTitle, productType, productBrand, packagingQuantityText } from '@/utils/pcProductDisplay'
  import { normalizeProductFiles, isImageFile, isPdfFile } from '@/utils/pcProductFiles'

  const pcIcons = {
    right: require('@/assets/icons/pc/chevron-right.svg'),
    plus: require('@/assets/icons/pc/plus.svg'),
    minus: require('@/assets/icons/pc/minus.svg'),
    file: require('@/assets/icons/pc/invoice.svg')
  }

  export default {
    name: 'PcProductDetail',
    components: { QuantityInput },
    data() {
      return {
        product: commerce.normalizeProduct({}),
        activeImageIndex: 0,
        previewVisible: false,
        carouselTimer: null,
        quantity: 1,
        params: [],
        files: [],
        activeDetailTab: 'params',
        icons: pcIcons
      }
    },
    computed: {
      productCode() {
        return `BM${String(this.product.id || this.product.productId || 1).padStart(10, '0')}`
      },
      displayTiers() {
        if (!this.product.hasPrice) return []
        return this.product.priceTiers.length ? this.product.priceTiers : [{ minQuantity: this.product.min, price: this.product.price }]
      },
      currentPrice() {
        return commerce.formatMoney(commerce.getTierPrice(this.product, this.quantity), 4)
      },
      totalAmount() {
        return commerce.formatMoney(Number(this.currentPrice) * this.quantity, 2)
      },
      canPurchase() {
        return this.product.hasPrice && Number(this.product.stock || 0) >= Number(this.product.min || 1)
      },
      purchaseButtonText() {
        if (!this.product.hasPrice) return '联系询价'
        return this.canPurchase ? '加入购物车' : '库存不足'
      },
      activeGalleryImage() {
        return this.product.images[this.activeImageIndex] || this.product.image
      },
      baseInfo() {
        return [
          { label: '产品类型', value: productType(this.product) || '-' },
          { label: '品牌', value: productBrand(this.product) },
          { label: '批次', value: this.product.batchNo || '-' },
          { label: '型号', value: this.product.model || '-' },
          { label: '封装', value: this.product.package || '-' },
          { label: '包装数量', value: packagingQuantityText(this.product) },
          { label: '重量', value: this.product.weightGrams == null ? '-' : this.product.weightGrams + 'g' },
          { label: '分类', value: `${this.product.categoryName || '-'} / ${this.product.subCategoryName || '-'}` },
          { label: '描述', value: this.product.desc }
        ]
      },
      detailParams() {
        return this.params
      },
      imageFiles() {
        return this.files.filter(this.isImageFile)
      },
      pdfFiles() {
        return this.files.filter(this.isPdfFile)
      },
      downloadFiles() {
        return this.files.filter(file => !this.isImageFile(file) && !this.isPdfFile(file))
      }
    },
    created() {
      this.loadDetail()
    },
    mounted() {
      this.startCarousel()
    },
    beforeDestroy() {
      this.stopCarousel()
    },
    methods: {
      productTitle,
      isImageFile,
      isPdfFile,
      goSourcing() {
        const model = String(this.product.model || '').trim()
        this.$router.push({ path: '/pc/sourcing', query: model ? { model } : {} })
      },
      loadDetail() {
        const id = decodeRouteCode('product', this.$route.params.code)
        if (!id) {
          this.product = this.normalizeProduct({})
          this.quantity = this.product.min
          this.params = []
          this.files = []
          this.activeImageIndex = 0
          this.activeDetailTab = this.requestedDetailTab()
          return
        }
        getPcProductDetail(id, createPcTrackingEvent('view')).then(data => {
          const product = data && data.product ? data.product : data
          this.product = this.normalizeProduct(product || {})
          this.quantity = this.product.min
          this.params = this.normalizeParams(product || {}, (data && data.params) || (product && product.params) || [])
          this.files = normalizeProductFiles((data && data.files) || (product && product.files) || [])
          this.activeImageIndex = 0
          this.activeDetailTab = this.requestedDetailTab()
          this.$nextTick(() => {
            this.startCarousel()
            this.scrollToRequestedSection()
          })
        }).catch(() => {
          this.product = this.normalizeProduct({})
          this.quantity = this.product.min
          this.params = []
          this.files = []
          this.activeImageIndex = 0
        })
      },
      requestedDetailTab() {
        return this.$route && this.$route.query && this.$route.query.tab === 'files' ? 'files' : 'params'
      },
      scrollToRequestedSection() {
        if (this.requestedDetailTab() !== 'files' || typeof document === 'undefined') return
        const section = document.getElementById('product-files')
        if (section) section.scrollIntoView({ block: 'start' })
      },
      normalizeProduct(item) {
        const product = commerce.normalizeProduct(item)
        return {
          ...product,
          categoryName: item.categoryName || product.categoryName || '',
          subCategoryName: item.subCategoryName || product.subCategoryName || '',
          model: item.model || ''
        }
      },
      normalizeParams(product, params) {
        const rows = Array.isArray(params) ? params.filter(Boolean) : []
        const brandParam = rows.find(item => ['品牌', '厂牌'].indexOf(item.paramName || item.name) !== -1)
        const brand = product.brand || (brandParam && (brandParam.paramValue || brandParam.value))
        const base = [
          { name: '产品大项', value: product.categoryName || '-' },
          { name: '产品种类', value: product.subCategoryName || '-' },
          { name: '产品类型', value: productType(product) || '-' },
          { name: '品牌', value: productBrand({ brand }) },
          { name: '批次', value: product.batchNo || '-' },
          { name: '型号', value: product.model || '-' },
          { name: '包装数量', value: packagingQuantityText(product) },
          { name: '重量', value: product.weightGrams == null ? '-' : product.weightGrams + 'g' },
          ...(product.packageName ? [{ name: '封装', value: product.packageName }] : [])
        ]
        const extra = rows
          .filter(item => item && ['封装', '通用封装', '封装规格', '厂牌', '品牌', '批次', '产品批次', '包装数量'].indexOf(item.paramName || item.name) === -1)
          .map(item => ({
            name: item.paramName || item.name,
            value: item.paramValue || item.value || item.unit || '-'
          }))
        // 后台添加的额外参数优先展示，系统基本参数置于后方。
        return extra.concat(base)
      },
      selectImage(index) {
        if (index < 0 || index >= this.product.images.length) return
        this.activeImageIndex = index
      },
      prevImage() {
        const total = this.product.images.length
        if (total <= 1) return
        this.activeImageIndex = (this.activeImageIndex - 1 + total) % total
      },
      nextImage() {
        const total = this.product.images.length
        if (total <= 1) return
        this.activeImageIndex = (this.activeImageIndex + 1) % total
      },
      startCarousel() {
        this.stopCarousel()
        if (this.product.images.length <= 1) return
        this.carouselTimer = window.setInterval(() => this.nextImage(), 4000)
      },
      stopCarousel() {
        if (this.carouselTimer) {
          window.clearInterval(this.carouselTimer)
          this.carouselTimer = null
        }
      },
      handleProductImageError(event) {
        commerce.handleProductImageError(event)
      },
      handleGalleryImageError(index) {
        this.$set(this.product.images, index, commerce.PRODUCT_PLACEHOLDER)
      },
      changeQuantity(delta) {
        this.quantity = commerce.normalizeQuantity(commerce.toNumber(this.quantity, this.product.min || 1) + delta, this.product.min, this.product.stock)
      },
      handleQuantityInput(value) {
        this.quantity = commerce.normalizeQuantity(value, this.product.min, this.product.stock)
      },
      addCurrentToCart() {
        if (!this.product.hasPrice) return this.goSourcing()
        if (!this.canPurchase) return message.warning('该商品库存不足，暂时无法购买')
        addCartItem(this.product, this.quantity)
        this.$emit('cart-change')
        message.success('已加入购物车')
      },
      buyNow() {
        if (!this.canPurchase) return message.warning('该商品库存不足，暂时无法购买')
        prepareBuyNowItem(this.product, this.quantity)
        this.$emit('cart-change')
        this.$router.push('/pc/checkout')
      }
    }
  }
</script>

<style scoped>
  .pc-detail {
    padding-bottom: 70px;
    background: #f3f5f9;
  }

  .breadcrumb {
    height: 42px;
    color: #667085;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .breadcrumb img {
    width: 12px;
    height: 12px;
  }

  .breadcrumb .current {
    color: #8a8f99;
  }

  .detail-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 380px;
    gap: 16px;
  }

  .main-column {
    display: grid;
    gap: 16px;
  }

  .summary-card,
  .param-card,
  .purchase-card {
    border-radius: 8px;
    background: #fff;
  }

  .summary-card {
    display: grid;
    grid-template-columns: 320px minmax(0, 1fr);
    gap: 22px;
    min-height: 440px;
    padding: 16px;
  }

  .gallery-stage {
    position: relative;
  }

  .main-image {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    border-radius: 4px;
    object-fit: contain;
    background: #f6f7fb;
    cursor: zoom-in;
  }

  .image-preview-mask{position:fixed;z-index:2000;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.82)}.image-preview-mask>img{max-width:88vw;max-height:88vh;object-fit:contain}.preview-close,.preview-arrow{position:absolute;border:0;background:rgba(255,255,255,.14);color:#fff;cursor:pointer}.preview-close{top:24px;right:30px;width:46px;height:46px;border-radius:50%;font-size:34px}.preview-arrow{top:50%;width:52px;height:72px;transform:translateY(-50%);font-size:48px}.preview-prev{left:30px}.preview-next{right:30px}

  .thumb-row {
    height: 54px;
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
  }

  .thumb-row img {
    width: 44px;
    height: 44px;
    border: 1px solid #d8dde7;
    border-radius: 4px;
    object-fit: contain;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .thumb-row img.active {
    border-color: #1684ff;
    box-shadow: 0 0 0 1px #1684ff;
  }

  .thumb-row .arrow {
    border: 0;
    background: #fff;
    color: #b0b7c3;
    font-size: 42px;
    line-height: 1;
    cursor: pointer;
    flex: 0 0 28px;
  }

  .thumb-row .arrow:disabled {
    cursor: default;
    opacity: .35;
  }

  .stage-arrow {
    position: absolute;
    top: 50%;
    width: 34px;
    height: 48px;
    padding: 0;
    transform: translateY(-50%);
    border: 0;
    border-radius: 4px;
    background: rgba(17, 24, 39, .38);
    color: #fff;
    font-size: 34px;
    line-height: 44px;
    cursor: pointer;
  }

  .stage-arrow.prev { left: 8px; }
  .stage-arrow.next { right: 8px; }

  .gallery-counter {
    position: absolute;
    right: 10px;
    bottom: 9px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(17, 24, 39, .52);
    color: #fff;
    font-size: 12px;
  }

  .gallery p {
    margin: 4px 0 0;
    text-align: center;
    color: #8a8f99;
    font-size: 12px;
  }

  .summary-info {
    padding: 2px 0;
  }

  .title-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e5e7eb;
  }

  .title-row h1 {
    margin: 0;
    color: #344054;
    font-size: 26px;
    line-height: 32px;
  }

  .summary-info p {
    margin: 7px 0 9px;
    color: #475569;
    font-size: 16px;
    line-height: 22px;
  }

  .summary-info dl {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr);
    gap: 7px 0;
    margin: 0;
    color: #4b5563;
    font-size: 16px;
    line-height: 22px;
  }

  .summary-info dt {
    color: #667085;
  }

  .summary-info dd {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .purchase-card {
    min-height: 508px;
    padding: 18px 16px;
  }

  .purchase-card h2 {
    margin: 0 0 14px;
    color: #111827;
    font-size: 15px;
  }

  .price-table {
    margin-bottom: 18px;
    padding-bottom: 10px;
    border-radius: 6px;
    background: #f7f7f7;
  }

  .price-head,
  .price-table p {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }

  .price-head {
    height: 34px;
    background: #eee;
    text-align: center;
    font-weight: 700;
  }

  .price-table p {
    margin: 0;
    height: 31px;
    text-align: center;
    color: #344054;
  }

  .price-table b,
  .price-table .first,
  .quantity-line b,
  .total-line b {
    color: #ff4d00;
    font-weight: 400;
  }

  .price-table button {
    display: block;
    height: 26px;
    margin: 2px auto 0;
    border: 1px solid #d8dde7;
    border-radius: 4px;
    background: #fff;
    color: #344054;
  }

  .stock-table {
    margin-bottom: 18px;
    border: 1px solid #d8dde7;
    border-radius: 5px;
    overflow: hidden;
  }

  .stock-table div {
    height: 28px;
    background: #f0f0f0;
    font-weight: 700;
  }

  .stock-table div,
  .stock-table p {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .stock-table span,
  .stock-table b {
    padding: 0 10px;
  }

  .stock-table p {
    margin: 0;
    height: 30px;
  }

  .buy-info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .quantity-line {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .stepper {
    width: 126px;
    height: 32px;
    display: grid;
    grid-template-columns: 32px 1fr 32px;
    border: 1px solid #d5d9e2;
    border-radius: 6px;
    overflow: hidden;
  }

  .stepper button,
  .stepper input {
    min-width: 0;
    height: 100%;
    box-sizing: border-box;
    border: 0;
    border-right: 1px solid #d5d9e2;
    background: #fff;
    text-align: center;
    outline: 0;
  }

  .stepper button:last-child {
    border-right: 0;
  }

  .stepper button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
  }

  .stepper input {
    width: 100%;
    padding: 0 4px;
    font-size: 15px;
    line-height: 30px;
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .stepper input::-webkit-outer-spin-button,
  .stepper input::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }

  .stepper button img {
    width: 13px;
    height: 13px;
  }

  .total-line {
    margin-top: 20px;
    font-size: 16px;
    font-weight: 700;
  }

  .total-line b {
    font-size: 22px;
    font-weight: 700;
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 16px;
  }

  .actions button {
    height: 48px;
    border: 1px solid #1684ff;
    border-radius: 4px;
    background: #fff;
    color: #1684ff;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
  }

  .actions .buy {
    background: #1684ff;
    color: #fff;
  }

  .param-card {
    overflow: hidden;
  }

  .tabs {
    height: 40px;
    display: flex;
    align-items: flex-end;
    border-bottom: 1px solid #d8dde7;
  }

  .tabs button {
    width: 120px;
    height: 40px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: #fff;
    font-weight: 700;
    cursor: pointer;
  }

  .tabs .active {
    border-bottom-color: #1684ff;
    color: #1684ff;
  }

  .param-card h2 {
    margin: 24px 16px;
    font-size: 15px;
  }

  .file-content,
  .product-summary,
  .detail-empty {
    padding: 0 16px 24px;
  }

  .pdf-list {
    display: grid;
    gap: 20px;
  }

  .pdf-preview {
    width: 100%;
  }

  .pdf-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px;
    border: 1px solid #d8dde7;
    border-bottom: 0;
  }

  .pdf-toolbar span { overflow-wrap: anywhere; }
  .pdf-toolbar a { flex-shrink: 0; color: #1684ff; }

  .pdf-preview iframe {
    display: block;
    width: 100%;
    height: 760px;
    border: 1px solid #d8dde7;
    border-radius: 0 0 7px 7px;
    background: #f3f4f6;
    box-sizing: border-box;
  }

  .file-image-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
  }

  .file-image-grid a {
    color: #344054;
  }

  .file-image-grid img {
    display: block;
    width: 100%;
    height: 190px;
    margin-bottom: 7px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    object-fit: contain;
  }

  .download-list {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .download-list a {
    color: #1684ff;
  }

  .download-list img {
    width: 16px;
    height: 16px;
    margin-right: 7px;
    vertical-align: -3px;
  }

  .product-summary {
    min-height: 120px;
    color: #475569;
    line-height: 28px;
    white-space: pre-wrap;
  }

  .detail-empty {
    color: #8a8f99;
  }

  .product-overview {
    margin: 0 16px 18px;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fafbfc;
  }

  .product-overview h3 {
    margin: 0 0 8px;
    color: #344054;
    font-size: 15px;
  }

  .product-overview p {
    margin: 0;
    color: #475569;
    line-height: 24px;
    white-space: pre-wrap;
  }

  .param-table {
    padding: 0 16px 22px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    height: 32px;
    border: 1px solid #d8dde7;
    padding: 0 10px;
    text-align: left;
  }

  th {
    background: #f7f7f7;
  }
</style>
